'use client'

import { useRouter } from 'next/navigation'

import * as DropdownMenu from '@components/ui/dropdown-menu'
import IconCheckmark from '@images/icons/check.svg'
import IconChevronDown from '@images/icons/chevron-down.svg'
import { cn } from '@lib/cn'
import { ENVIRONMENT_LABELS, environmentDotClass, switchEnvironment } from '@lib/environment'
import { ENVIRONMENT_KINDS, useAppRoute, type EnvironmentKind } from '@lib/use-app-route'

const EnvironmentMenu = ({ className, variant = 'menu' }: { className?: string; variant?: 'menu' | 'chip' }) => {
  const router = useRouter()
  const { pathname, teamSlug, envKind } = useAppRoute()

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Switch environment"
          className={cn(
            'flex shrink-0 items-center gap-1.5 outline-hidden',
            variant === 'menu' && 'h-9 rounded-lg px-2.5 text-sm text-o-slate hover:bg-o-chip data-[state=open]:bg-o-chip',
            variant === 'chip' && 'h-[38px] rounded-full bg-o-track px-3.5 text-[12.5px] text-o-ink',
            className
          )}
        >
          <span className={cn('shrink-0', variant === 'chip' ? 'size-[7px] rounded-xs' : 'size-2 rounded-full', environmentDotClass[envKind])} />
          <span data-testid="environment-badge" className="font-medium">
            {ENVIRONMENT_LABELS[envKind]}
          </span>
          {variant === 'menu' && <IconChevronDown className="size-2.5 fill-current opacity-60" />}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" className="w-[180px]">
        <DropdownMenu.RadioGroup
          value={envKind}
          onValueChange={(value) => switchEnvironment(router, pathname, teamSlug, value as EnvironmentKind)}
        >
          {ENVIRONMENT_KINDS.map((kind) => (
            <DropdownMenu.RadioItem key={kind} value={kind}>
              <span className={cn('size-1.5 shrink-0 rounded-full', environmentDotClass[kind])} />
              <span className="flex-1 text-o-ink">{ENVIRONMENT_LABELS[kind]}</span>
              {kind === envKind && <IconCheckmark className="size-3.5 shrink-0 fill-current text-o-muted" />}
            </DropdownMenu.RadioItem>
          ))}
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export default EnvironmentMenu
